import DOMPurify from 'isomorphic-dompurify';
import { isOwnStorageUrl } from './storage-url';
import { bioHtml, bioPlainText } from './profile-bio';
import { noteHtml } from './profile-notes';

/**
 * Strict HTML sanitization config shared by every render site that receives
 * user-authored HTML (Tiptap RTE output, FAQ answers, etc.). The allowlist
 * matches what the rich-text editor produces — if you extend the editor, add
 * the new tags here, not the other way around.
 *
 * Sanitization runs on BOTH the server and the client. Server-side
 * sanitization at render time is what closes the SSR-XSS window: Next.js
 * paints the server-rendered HTML to the browser before client JS hydrates,
 * so any deferred-to-client sanitization is too late — the malicious
 * `<img onerror=...>` already fired during initial paint. `isomorphic-
 * dompurify` ships a jsdom-backed implementation for Node so the same
 * sanitize() call works in both environments.
 *
 * The Firestore triggers in functions/src/index.ts also sanitize on write,
 * but that only protects records written *after* the trigger went live;
 * legacy records and any race-window writes still depend on this render-
 * time pass.
 */

const STRICT_HTML_CONFIG_INTERNAL = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 's',
    'h1', 'h2', 'h3', 'h4',
    'ul', 'ol', 'li',
    'a', 'blockquote', 'code', 'pre',
    'span',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel',
    // line and paragraph spacing, on a paragraph or a heading. Every editor
    // mount has the spacing control (tier descriptions, which this list
    // serves, as well as the description, profile notes and the bio), so
    // every sanitizer keeps it. Two named data attributes rather than an
    // inline `line-height` or `margin` because `style` is stripped here.
    // Listing them by name admits exactly these two while ALLOW_DATA_ATTR stays
    // false for every other data attribute, and the block-attribute hook drops
    // any value outside ALLOWED_LINE_HEIGHTS or ALLOWED_PARAGRAPH_SPACINGS and
    // any spacing on a tag that is not a text block. Inherited by the rich list
    // below through its spread.
    'data-line-height', 'data-paragraph-spacing',
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Exact attribute names only — FORBID_ATTR is a list, NOT a glob, so the
  // `'on*'` that used to sit here matched nothing and was pure decoration. It
  // also made this list differ from the functions-side mirror for no reason.
  // Event handlers are stripped by DOMPurify regardless of this list.
  FORBID_ATTR: ['style', 'class', 'id'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'style', 'link', 'meta'],
  ALLOW_DATA_ATTR: false,
};

// The bundled `@types/dompurify` lags the v3 runtime and declares a Config
// type that mismatches what dompurify itself exports. Cast through `unknown`
// to the runtime's own Config type so the call sites still compile.
type PurifyConfig = Parameters<typeof DOMPurify.sanitize>[1];
export const STRICT_HTML_CONFIG =
  STRICT_HTML_CONFIG_INTERNAL as unknown as PurifyConfig;

/**
 * The WIDER allowlist, used for TWO fields: a product's `longDescription` and,
 * since 2026-10-09, a seller's profile notes (`sanitizeNoteHtml`).
 *
 * WHY A SECOND CONFIG RATHER THAN WIDENING THE FIRST. The strict list above is
 * shared by the rest of the user-authored HTML on the site: FAQ answers and
 * price-tier descriptions. Those are short annotations written into someone
 * else's frame, and neither has any business carrying an image, a table or an
 * embedded player. Widening the shared list would hand those powers to every
 * field that uses it at once. So each widening is scoped to the field that
 * asked for it (notes were moved onto this list by owner decision, with their
 * own upload pipeline), and `sanitizeHtml` keeps meaning exactly what it meant
 * before.
 *
 * WHAT IS ADDED, AND WHY EACH IS SAFE:
 *   - `hr`: inert.
 *   - `img`: constrained further by the SOURCE HOOK below to our own storage
 *     BUCKET. Left unconstrained it would be a tracking pixel: a seller could
 *     log the IP and user-agent of every visitor to their product page by
 *     pointing an image at a host they control. CSP's img-src would block the
 *     load in production, but a sanitizer that depends on a response header to
 *     be safe is one deploy away from not being safe, so the rule is enforced
 *     here too — and CSP could not enforce it anyway, being host-scoped when
 *     the two Google Storage hosts front every bucket on the platform.
 *   - the table family: inert layout elements. `colwidth` is what Tiptap emits
 *     for a sized column; it is a plain numeric attribute, not a style.
 *   - `iframe`: the one genuinely dangerous addition, and the reason
 *     `registerMediaSourceHook` exists. Only the two video-player origins pass,
 *     the element is hardened on the way through, and anything else is removed
 *     from the tree entirely rather than merely stripped of its src.
 *
 * Everything the strict config forbids stays forbidden here: no `style`, no
 * `class`, no `id`, no `script`, no `form`. Data attributes stay off as a
 * class (ALLOW_DATA_ATTR is false); the only two admitted are the spacing pair,
 * named one by one in ALLOWED_ATTR and value-checked by a hook, so a pasted
 * `data-anything` is still stripped.
 */
const RICH_HTML_CONFIG_INTERNAL = {
  ALLOWED_TAGS: [
    ...STRICT_HTML_CONFIG_INTERNAL.ALLOWED_TAGS,
    'hr',
    'img',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
    'iframe',
  ],
  ALLOWED_ATTR: [
    ...STRICT_HTML_CONFIG_INTERNAL.ALLOWED_ATTR,
    // img
    'src', 'alt', 'width', 'height', 'loading',
    // table
    'colspan', 'rowspan', 'colwidth',
    // iframe. `sandbox` and `referrerpolicy` are listed because the hook SETS
    // them; without them on the allowlist DOMPurify would strip the very
    // hardening the hook had just applied.
    'allow', 'allowfullscreen', 'title', 'frameborder', 'sandbox', 'referrerpolicy',
    // alignment, on a paragraph, a heading, an image or an embed. The legacy
    // `align` attribute rather than a class or an inline style for exactly the
    // reason `width` is one: both of those are stripped here, so a plain
    // attribute is the only channel through which a seller's layout choice
    // reaches the buyer's page. Its value, and the tag carrying it, are
    // re-validated by the hook below.
    'align',
    // (Line and paragraph spacing come in through the spread above: the strict
    // list carries them, because every editor mount has the control.)
    //
    // the number an ordered list starts at, which the editor writes when a
    // list is begun by typing "3. ". Kept only on an `ol` and only as a whole
    // number (enforceListStart); see LIST_START_RE.
    'start',
  ],
  ALLOWED_URI_REGEXP: STRICT_HTML_CONFIG_INTERNAL.ALLOWED_URI_REGEXP,
  FORBID_ATTR: STRICT_HTML_CONFIG_INTERNAL.FORBID_ATTR,
  // `iframe` is deliberately absent from this list, unlike in the strict config
  // above. FORBID_TAGS beats ALLOWED_TAGS in DOMPurify, so leaving it here
  // would make the tag unreachable no matter what the allowlist says. Its
  // safety comes from registerMediaSourceHook, not from this line.
  FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'style', 'link', 'meta'],
  ALLOW_DATA_ATTR: false,
};

export const RICH_HTML_CONFIG =
  RICH_HTML_CONFIG_INTERNAL as unknown as PurifyConfig;

/**
 * The rich config again, with ONE relaxation: `blob:` is a legal image source.
 *
 * WHY. A description image is not uploaded the moment the seller picks it —
 * it rides the product's existing upload pipeline and is uploaded, alongside
 * the cover and the gallery, when the product is saved. Until then the editor
 * holds a `blob:` URL for it. That is the right trade (one upload path, one
 * progress bar, one quota check, and nothing orphaned in Storage if the seller
 * abandons the form), but it means the seller's own "preview the page" view is
 * looking at HTML whose images are not yet on a storage host.
 *
 * Without this config that preview would silently drop every unsaved image and
 * show the seller a page that is not the page they are about to publish. So the
 * preview gets a config that tolerates the in-flight state.
 *
 * This is NEVER the config a stored or buyer-visible document goes through.
 * `blob:` URLs are per-document and per-session: one cannot be authored by
 * anyone but the person viewing the page, and it resolves to nothing at all in
 * any other browser. The rest of the allowlist — no script, no style, no data
 * attributes, the same iframe hook — is identical.
 */
const PREVIEW_RICH_CONFIG_INTERNAL = {
  ...RICH_HTML_CONFIG_INTERNAL,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|blob):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

const PREVIEW_RICH_CONFIG =
  PREVIEW_RICH_CONFIG_INTERNAL as unknown as PurifyConfig;

/**
 * The allowlist for ONE field: a user's `bio`.
 *
 * WHY A THIRD CONFIG. The bio is written in the same RichTextEditor as a
 * product's long description, in its `blockFormatting` mode: the text marks,
 * headings and lists every editor has, plus alignment and line and paragraph
 * spacing, and none of the media. Neither existing config fits that. The
 * strict one strips `align` and the spacing attributes, so a seller would
 * watch their layout vanish on save; the rich one admits images, tables,
 * dividers and video frames, none of which the bio editor can produce, and a
 * forged write to the world-readable user document should not be able to put
 * them on a profile page either.
 *
 * WHAT IS LEFT OUT OF THE STRICT TAG SET, AND WHY:
 *   - `a`. The bio editor has no link control, so an anchor in a bio can only
 *     have come from a hand-written write. Off-site links on a profile already
 *     have a vetted channel, the social links, whose every URL is checked
 *     against its platform (validateSocialLinkUrl) before it renders; an
 *     arbitrary `<a href>` in the bio would be a way round that. DOMPurify
 *     keeps the anchor's TEXT, so nothing a reader could see is lost.
 *   - `span`. Nothing the editor emits uses one, and without `class` or
 *     `style` it carries nothing.
 *
 * NO ARIA. DOMPurify admits `aria-*` by default, and nothing the editor
 * writes carries one, so in a bio one could only have been forged, to hide
 * text from screen readers (`aria-hidden`), to shout it (`aria-live`), or to
 * pull other elements of the page into the bio's accessibility subtree
 * (`aria-owns`). ALLOW_ARIA_ATTR is off, so every attribute a bio keeps has a
 * closed list of values.
 *
 * NO WRITE-SIDE COPY. Unlike the description, the bio has no Firestore trigger
 * re-sanitizing it: it is written straight from the client to users/{uid},
 * and firestore.rules bounds only its length. So this render-time pass is the
 * only gate on its markup, the same position profile notes are in, and every
 * surface that renders a bio has to go through it (ProfileBio in
 * src/components/profile/profile-bio.tsx is that one surface).
 */
const BIO_HTML_CONFIG_INTERNAL = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 's',
    'h1', 'h2', 'h3', 'h4',
    'ul', 'ol', 'li',
    'blockquote', 'code', 'pre',
  ],
  ALLOWED_ATTR: ['align', 'data-line-height', 'data-paragraph-spacing', 'start'],
  ALLOWED_URI_REGEXP: STRICT_HTML_CONFIG_INTERNAL.ALLOWED_URI_REGEXP,
  FORBID_ATTR: STRICT_HTML_CONFIG_INTERNAL.FORBID_ATTR,
  FORBID_TAGS: STRICT_HTML_CONFIG_INTERNAL.FORBID_TAGS,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

const BIO_HTML_CONFIG =
  BIO_HTML_CONFIG_INTERNAL as unknown as PurifyConfig;

/**
 * Whether an `<img>` src points at an object in OUR OWN storage bucket.
 *
 * Deliberately a bucket check rather than a host check. Both
 * `firebasestorage.googleapis.com` and `storage.googleapis.com` front EVERY
 * bucket on Google Cloud, not just ours, so a host-only allowlist would accept
 * `https://storage.googleapis.com/some-attacker-bucket/pixel.gif` as one of
 * ours — which is the tracking pixel this rule exists to stop, wearing a
 * hostname we trust. It would also pass CSP's img-src, which is host-scoped for
 * the same unavoidable reason. src/lib/storage-url.ts already carries this
 * distinction and the reasoning behind it, for /api/image-proxy.
 *
 * The bucket being ours is also what makes a description image die with its
 * product: the soft-delete sweep in onProductUpdated clears the whole
 * `products/{id}/` prefix, and it can only clear objects we own.
 */
/**
 * Exported so the EDITOR can refuse the same sources at paste time. A rule the
 * sanitizer applies at save and the editor does not apply at paste is a rule
 * the seller meets as content disappearing.
 */
export function isOwnStorageImage(src: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(src);
  } catch {
    // Relative, blob:, data: or malformed. A description image is always an
    // absolute https URL on our storage bucket by the time it is saved, so
    // anything unparseable here is either an unfinished editor state that must
    // not persist or an attempt at something else.
    return false;
  }
  // https ONLY. `isOwnStorageUrl` answers "whose bucket is this?" and is
  // deliberately silent about the scheme, so on its own it accepts
  // `http://firebasestorage.googleapis.com/...`. That URL is doubly dead at
  // render — mixed content on an https page, and CSP's img-src entries are
  // https-scoped — so keeping it only ever produces a broken image the seller
  // cannot explain. The preview resolver already requires https for the same
  // reason (isPreviewSrcOnStorageHost); this brings images into line.
  if (parsed.protocol !== 'https:') return false;
  // Only the two hostnames CSP img-src actually lists. `isOwnStorageUrl` also
  // recognises the virtual-hosted `<bucket>.storage.googleapis.com` form, which
  // is genuinely one of our buckets but matches no img-src entry (there is no
  // wildcard for it), so an image stored in that shape would survive this
  // sanitizer and then be refused by the browser. Rejecting it here means the
  // seller loses the image at save time, with the editor's own preview showing
  // the same result, rather than discovering a blank on the live page.
  const host = parsed.hostname.toLowerCase();
  if (host !== 'firebasestorage.googleapis.com' && host !== 'storage.googleapis.com') return false;
  return isOwnStorageUrl(parsed);
}

/**
 * The only two embed URLs an `<iframe>` may carry. Matched on the WHOLE URL
 * rather than the host, so `https://www.youtube-nocookie.com/@someone` cannot
 * be framed: only a player at a specific video id can.
 *
 * youtube-NOCOOKIE rather than youtube.com, because the plain domain sets its
 * tracking cookies on load, before the visitor has pressed anything. That would
 * make every product page carrying a video a page that needs a consent gate it
 * does not have. The editor writes the nocookie form; this is what enforces it.
 *
 * Both hosts must also appear in the CSP frame-src (src/middleware.ts) or the
 * browser blocks the frame regardless of what this allows.
 */
/**
 * How many images one product's description may carry.
 *
 * WHY THERE IS A NUMBER AT ALL. Every other kind of product media is bounded by
 * something: the cover and the hero are one object each, the gallery has
 * MEDIA_MAX_COUNT, deliverables are metered against the seller's tier cap and
 * the free-tier byte ceiling. Description images are bounded by none of it.
 * They are deliberately outside allProductPaths and outside
 * onProductFileFinalized's reactive cap — adding them would make attaching a
 * banner "grow the file set" and cost a grandfathered listing its exemption —
 * which left the per-object 10MB in storage.rules as the only server-side limit
 * on a world-readable prefix. A toolbar button with no ceiling, writing to a
 * prefix with no quota, is anonymous file hosting with extra steps. This is the
 * ceiling.
 *
 * Five, because this is a product description and not an album.
 *
 * PROFILE NOTES SHARE IT, per note: `sanitizeNoteHtml` is this pass, and the
 * notes composer refuses the sixth pick the way the product forms do. Their
 * images live under users/{uid}/notes/ in Storage, bounded there by the same
 * per-object 10MB and, across a profile, by the notes' total size
 * (NOTES_MAX_TOTAL_BYTES), which caps how many image links the notes can hold.
 *
 * Enforced in three places, and the order is the point. Both product forms
 * refuse the sixth pick before the file is ever read off disk, so a seller
 * meets the limit as a message rather than as content that quietly vanishes.
 * `sanitizeRichHtml` in functions drops anything past the fifth on the way in,
 * which is what binds a client that did not ask nicely. And the copy here
 * applies the same trim at render, so a document written before this shipped is
 * capped when it is displayed rather than needing a migration.
 *
 * VIDEO EMBEDS ARE NOT COUNTED. This is a storage bound, and an embed is an
 * iframe pointing at YouTube or Vimeo: it costs no bytes of ours and no egress
 * of ours, so charging it against a limit that exists to bound our own bucket
 * would be a restriction with nothing behind it.
 *
 * Mirrored by the constant of the same name in functions/src/lib/sanitize-html.ts.
 * Keep the two in step.
 */
export const DESCRIPTION_IMAGE_MAX_COUNT = 5;

/**
 * How many video embeds one product's description may carry.
 *
 * The note on DESCRIPTION_IMAGE_MAX_COUNT above explains why embeds are not
 * counted against the IMAGE cap: an embed costs none of our bytes, so a limit
 * that exists to bound our own bucket has nothing to say about it. That
 * remains true and is not what this number is for.
 *
 * This one bounds the PAGE. An embed is a third-party iframe on the most
 * visited page in the app, and the cost of an unbounded number of them is paid
 * by the buyer's browser rather than by our storage: layout, memory, and a
 * document that every marketplace listener downloads in full. Measured before
 * this existed, five hundred iframes survived both sanitizers untouched.
 * `loading="lazy"` (set on every surviving frame below) keeps the network cost
 * down but does nothing about the DOM.
 *
 * Five, for the same reason as the images: this is a product description.
 * Profile notes share it per note, as they share the image cap.
 *
 * Enforced in the same three places and the same order — both forms refuse the
 * sixth as a message, the functions copy binds any client that did not ask
 * nicely, and this copy trims at render so a document written before the cap
 * existed is capped when displayed rather than needing a migration.
 *
 * Mirrored by the constant of the same name in functions/src/lib/sanitize-html.ts.
 */
export const DESCRIPTION_EMBED_MAX_COUNT = 5;

/**
 * Hard ceiling on the raw HTML of one `longDescription`, in characters.
 *
 * DESCRIPTION_SOFT_LIMIT (src/lib/product-form-shared.ts) counts VISIBLE
 * characters and is what the seller sees as "1,234 / 5,000". It was never
 * enforced anywhere — not by the forms, not here, not by firestore.rules — so
 * the only real bound on the field was Firestore's 1MB document limit, and a
 * 900KB description was measured passing both sanitizers unchanged.
 *
 * That matters more than it looks. The marketplace listener in
 * src/context/product-context.tsx reads up to CATALOG_MAX_DOCS whole product
 * documents, and the Firestore web SDK has no field projection, so one seller's
 * oversized description is downloaded by every visitor to the marketplace.
 *
 * This is the fence, and it is deliberately far above the soft limit rather
 * than equal to it: 5,000 visible characters plus heading markup, table markup,
 * five storage URLs (which are long) and five embed URLs lands comfortably
 * under 100,000. Nothing a seller can legitimately author reaches it, so the
 * cap bounds abuse without truncating real content.
 *
 * Applied by TRUNCATION rather than rejection, and before the sanitizer parse
 * rather than after: DOMPurify re-serialises from a parsed tree, so a cut that
 * lands mid-tag is closed and balanced on the way out rather than producing
 * broken markup.
 *
 * Mirrored in functions/src/lib/sanitize-html.ts, which is the binding copy.
 */
export const DESCRIPTION_HTML_MAX_LENGTH = 100_000;

/**
 * The only two embed URLs an `<iframe>` may carry, matched whole.
 *
 * These are exactly what `toEmbedUrl` (src/lib/video-embed.ts) produces and
 * nothing else, which is the point: the editor and the sanitizer are two halves
 * of one contract, and any slack between them is either a seller watching their
 * video vanish on save or a URL shape nobody designed.
 *
 * The QUERY STRING is what changed. Both patterns used to accept an arbitrary
 * `[A-Za-z0-9_\-=&;%]*` query, which let a hand-written `?autoplay=1&mute=1`
 * through — a video that starts playing by itself on a page the visitor opened
 * to read. The editor never produced one, so narrowing costs nothing: YouTube
 * embeds take no query at all, and Vimeo takes only the unlisted-video hash,
 * which the player genuinely needs (see the note in toEmbedUrl).
 */
const EMBED_URL_PATTERNS = [
  /^https:\/\/www\.youtube-nocookie\.com\/embed\/[A-Za-z0-9_-]{6,20}$/,
  /^https:\/\/player\.vimeo\.com\/video\/[0-9]{6,15}(\?h=[A-Za-z0-9]{6,20})?$/,
];
/**
 * The sandbox every embedded player carries.
 *
 * `allow-same-origin` IS REQUIRED, and leaving it out is what stopped these
 * embeds working at all. A sandboxed frame without it gets an OPAQUE origin,
 * and the consequences are fatal to a video player rather than cosmetic:
 * the player's own script throws the moment it touches localStorage or
 * document.cookie for its own origin, and the request it makes carries a null
 * Origin, which YouTube answers with "Video unavailable". The frame renders
 * black or empty, with nothing in it to explain why.
 *
 * IT IS NOT THE ESCAPE HATCH IT LOOKS LIKE. The usual warning about pairing
 * `allow-scripts` with `allow-same-origin` is that the framed document can then
 * reach into the embedder and remove its own sandbox attribute — but that
 * requires the frame to be SAME-ORIGIN WITH THE EMBEDDER, and these are not:
 * EMBED_URL_PATTERNS pins the src to youtube-nocookie.com and player.vimeo.com,
 * both cross-origin to supa.place. What the token actually grants a
 * cross-origin frame is the right to keep its OWN origin, and nothing of ours:
 * no DOM, no storage, no cookies, no auth token. The tokens that would matter
 * are still absent — no allow-top-navigation, no allow-forms, no allow-modals,
 * no allow-downloads, no allow-pointer-lock.
 *
 * Exported because five render sites and the mirror in
 * functions/src/lib/sanitize-html.ts all have to use the same string, and this
 * is a value where being subtly different in one place looks like a broken
 * video rather than a bug.
 */
export const EMBED_SANDBOX =
  'allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox';


/**
 * Is this an embed URL the platform will render?
 *
 * The same allowlist the description's iframes are held to, exported because
 * the GALLERY carries video items too. There the URL is not wrapped in HTML, so
 * it never passes through the sanitizer: it is a bare string in `images[]`, and
 * there is no `afterSanitizeAttributes` hook to catch it.
 *
 * THE ORDER OF THE TWO GUARDS, because this comment used to overstate its own
 * role and that is worth being exact about. A gallery frame is built by
 * `toEmbedUrl` (src/lib/video-embed.ts), which parses the stored string and
 * returns either `null` or one of two `https://` literals with a
 * regex-validated id interpolated. That normaliser is the primary guard, and it
 * is what makes the src safe. This function is the SECOND one: every render
 * site asserts the normaliser's output against the same patterns the sanitizer
 * enforces, so the frame is held to one allowlist whichever path produced its
 * src, and a future change to `toEmbedUrl` that widened what it emits would
 * fail here rather than reaching a buyer's page.
 *
 * Keep it called. An allowlist that nothing consults is not a control, and this
 * one spent a release documented as though it were.
 */
export function isAllowedEmbedUrl(src: unknown): boolean {
  return typeof src === 'string' && EMBED_URL_PATTERNS.some((re) => re.test(src));
}


/**
 * Percentage widths a seller may set on a description image or video.
 *
 * A CLOSED list rather than a range, and that is the point. `width` is a
 * presentational hint the browser applies directly, so an unvalidated value off
 * a stored document is a layout-injection surface: `width="2000"` on a page
 * with no horizontal overflow guard drags a scrollbar across the whole
 * document, and `width="1%"` makes an image the seller cannot find again in
 * their own editor. Five steps cover every intention anyone has for a picture
 * sitting in a paragraph.
 *
 * Percentages, never pixels: the description column is a different width on a
 * phone, in the standard two-column layout and in a full-width one, so a pixel
 * value chosen on a desktop overflows everywhere narrower.
 */
export const ALLOWED_MEDIA_WIDTHS: readonly string[] = ['25%', '40%', '50%', '75%', '100%'];

/**
 * The alignments a seller may set on a description block.
 *
 * A CLOSED list, on the same reasoning as `ALLOWED_MEDIA_WIDTHS` above. `align`
 * is a presentational hint the browser applies directly, and the legacy
 * attribute accepts a good deal more than these three: `justify`, which the
 * editor does not offer, and on an image `top`, `middle`, `bottom`, `texttop`
 * and `absmiddle`, none of which align anything horizontally and all of which
 * would reach the page as a layout nobody chose.
 *
 * `left` is written EXPLICITLY rather than being represented by the absence of
 * the attribute, which is the opposite of how "Full" width works. Absence
 * already means something for media: a sized image carrying no alignment is
 * centred by the render CSS, and has been since resizing shipped. Making
 * absence mean "left" would have silently re-laid-out every description already
 * saved with a narrowed image.
 *
 * MIRRORS `TEXT_ALIGN_OPTIONS` in src/components/rich-text-editor-extensions.ts.
 * A value the editor offers but this list rejects is a control that appears to
 * work and is undone on save, so the two are checked against each other by a
 * test.
 */
export const ALLOWED_ALIGNMENTS: readonly string[] = ['left', 'center', 'right'];

/**
 * The tags an `align` may sit on: the four block levels the editor can align,
 * plus the two media nodes. A `<td align="right">` is inert rather than
 * dangerous, but the editor never writes one and the render CSS does not style
 * one, so keeping it would mean storing an attribute nothing acts on.
 */
const ALIGNABLE_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'IMG', 'IFRAME']);

/**
 * Strip an alignment the allowlist does not recognise, or one sitting on a tag
 * that cannot carry it. The element itself always stays: an alignment is
 * presentation, and dropping a paragraph over it would cost the seller their
 * words to fix their layout.
 */
function enforceAlignment(el: Element): void {
  const align = el.getAttribute('align');
  if (align === null) return;
  if (!ALIGNABLE_TAGS.has(el.nodeName) || !ALLOWED_ALIGNMENTS.includes(align)) {
    el.removeAttribute('align');
  }
}

/**
 * The line spacings a seller may set on a paragraph or heading, as the
 * word-processor multiplier they picked ("1.5 spacing"), NOT as a CSS
 * `line-height`. globals.css maps each to the value that looks like that
 * spacing (single is a font's normal line height, about 1.2 in CSS), which is
 * why the stored value is the seller's intention and the render rule is where
 * the number lives.
 *
 * A CLOSED list, on the same reasoning as ALLOWED_ALIGNMENTS: the attribute is
 * a hook for a render rule, and a value no rule matches would be stored bytes
 * that do nothing. The ABSENCE of the attribute is the default spacing every
 * description had before this existed, so no stored document changes meaning.
 *
 * MIRRORS `LINE_HEIGHT_OPTIONS` in src/components/rich-text-editor-extensions.ts
 * (every non-null value there must be here and nothing else), and the copy of
 * this list in functions/src/lib/sanitize-html.ts. Tests check both.
 */
export const ALLOWED_LINE_HEIGHTS: readonly string[] = ['1', '1.15', '1.5', '2'];

/**
 * The gaps a seller may set around a paragraph or heading. Named rather than
 * measured for the reason ALLOWED_MEDIA_WIDTHS is a short list: the render
 * rule owns the number, and a free-form length would be a layout-injection
 * surface. Absence is the default gap, as above.
 *
 * MIRRORS `PARAGRAPH_SPACING_OPTIONS` in
 * src/components/rich-text-editor-extensions.ts and the functions copy.
 */
export const ALLOWED_PARAGRAPH_SPACINGS: readonly string[] = ['none', 'small', 'large'];

/**
 * The tags the spacing attributes may sit on: the text blocks the editor can
 * space (paragraphs and headings). Media and table cells are absent for the
 * reason ALIGNABLE_TAGS leaves out `td`: the editor never writes one there and
 * no render rule would act on it.
 */
const SPACEABLE_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4']);

/**
 * Strip a spacing value the allowlists do not recognise, or one sitting on a
 * tag that cannot carry it. The element always stays, as with alignment: this
 * is presentation, and dropping a paragraph over it would cost the author
 * their words.
 */
function enforceBlockSpacing(el: Element): void {
  const lineHeight = el.getAttribute('data-line-height');
  if (lineHeight !== null && (!SPACEABLE_TAGS.has(el.nodeName) || !ALLOWED_LINE_HEIGHTS.includes(lineHeight))) {
    el.removeAttribute('data-line-height');
  }
  const spacing = el.getAttribute('data-paragraph-spacing');
  if (spacing !== null && (!SPACEABLE_TAGS.has(el.nodeName) || !ALLOWED_PARAGRAPH_SPACINGS.includes(spacing))) {
    el.removeAttribute('data-paragraph-spacing');
  }
}

/**
 * The number an ordered list may start at: a whole number from 1 to 9999.
 *
 * Tiptap's ordered list writes `start` when a list is begun by typing "3. "
 * (and omits it for 1), and numbers the list from it in the editor. Without it
 * on the allowlist a list read 3, 4 while being written and 1, 2 once saved,
 * and reopened in the editor at 1. Nothing else about it is useful to keep, so
 * zero, negatives and anything that is not a plain number go. MIRRORED in
 * functions/src/lib/sanitize-html.ts.
 */
const LIST_START_RE = /^[1-9][0-9]{0,3}$/;

/** Strip a `start` that is not on an `ol` or is not a plain list number. */
function enforceListStart(el: Element): void {
  const start = el.getAttribute('start');
  if (start !== null && (el.nodeName !== 'OL' || !LIST_START_RE.test(start))) {
    el.removeAttribute('start');
  }
}

/** Strip a width the allowlist does not recognise, leaving the element full-width. */
function enforceMediaWidth(el: Element): void {
  const width = el.getAttribute('width');
  if (width !== null && !ALLOWED_MEDIA_WIDTHS.includes(width)) el.removeAttribute('width');
}

/** Detach a node from the tree, for the cases where clearing an attribute is not enough. */
function dropNode(el: Element): void {
  if (el.parentNode) el.parentNode.removeChild(el);
}

/**
 * Set for the duration of ONE `sanitizeRichHtmlForPreview` call, because
 * DOMPurify hooks are registered per-instance and cannot see which config the
 * current pass is using. Read only by the IMG branch below. Safe as a module
 * flag because `DOMPurify.sanitize` is synchronous: nothing can interleave
 * between the assignment and the reset in the `finally`.
 */
let allowLocalImageSources = false;

/**
 * Images still allowed through in the CURRENT rich pass, counted down by the
 * IMG branch below. Set by the two rich entry points and reset in their
 * `finally`, on the same reasoning as the flag above: `DOMPurify.sanitize` is
 * synchronous, so nothing can interleave between the assignment and the reset.
 *
 * Left at 0 between calls deliberately. `sanitizeHtml` forbids `img` outright,
 * so its pass never reaches the IMG branch at all; if some future config change
 * made it possible, a zero budget drops the image, which is the answer the
 * strict allowlist gives anyway.
 */
let richImageBudget = 0;

/**
 * Embeds still allowed through in the CURRENT rich pass, counted down by the
 * IFRAME branch below. Same lifecycle and the same reasoning as
 * `richImageBudget` above: set by the two rich entry points, reset in their
 * `finally`, and left at 0 between calls so the strict pass (which forbids
 * `iframe` outright) can never spend a budget it should not have.
 */
let richEmbedBudget = 0;

/**
 * Enforce the source rules for `<img>` and `<iframe>`, and harden the frame.
 *
 * Registered globally, like the anchor hook, because DOMPurify hooks are
 * per-instance rather than per-config. That is harmless for the strict config:
 * it forbids both tags outright, so neither branch is ever reached from
 * `sanitizeHtml`.
 *
 * Both branches REMOVE the element rather than clearing the offending
 * attribute. A surviving `<img>` with no src is a broken-image glyph in the
 * middle of a seller's description and a surviving `<iframe>` with no src is a
 * blank inset box; in both cases the author is shown a defect they cannot
 * explain instead of the content simply not being there.
 */
let mediaSourceHookRegistered = false;
function registerMediaSourceHook(): void {
  if (mediaSourceHookRegistered) return;
  mediaSourceHookRegistered = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const el = node as unknown as Element;
    if (typeof el.getAttribute !== 'function') return;

    if (el.nodeName === 'IMG') {
      const src = el.getAttribute('src') || '';
      // The seller's own preview of an image they have picked but not yet
      // saved. See PREVIEW_RICH_CONFIG_INTERNAL for why this state exists.
      const isLocalPreviewImage = allowLocalImageSources && src.startsWith('blob:');
      if (!isLocalPreviewImage && !isOwnStorageImage(src)) {
        // Dropped for its SOURCE, so it never reaches the budget below. An
        // image we were never going to render must not spend one of the five a
        // seller is allowed, or a single tracking pixel would cost them a real
        // image further down the page.
        dropNode(el);
        return;
      }
      // Past the fifth. Counted here rather than anywhere upstream because this
      // hook sees the images in document order after every other reason for
      // removal has been applied, which makes "the first five that survive" the
      // thing being kept. See DESCRIPTION_IMAGE_MAX_COUNT.
      if (richImageBudget <= 0) {
        dropNode(el);
        return;
      }
      richImageBudget -= 1;
      enforceMediaWidth(el);
      return;
    }

    if (el.nodeName === 'IFRAME') {
      if (!EMBED_URL_PATTERNS.some((re) => re.test(el.getAttribute('src') || ''))) {
        // Dropped for its SOURCE, before the budget below is touched, on the
        // same reasoning as the IMG branch: a frame we were never going to
        // render must not spend one of the five a seller is allowed.
        dropNode(el);
        return;
      }
      // Past the fifth. See DESCRIPTION_EMBED_MAX_COUNT.
      if (richEmbedBudget <= 0) {
        dropNode(el);
        return;
      }
      richEmbedBudget -= 1;
      // Hardening applied to every frame that survives, so it holds for
      // documents written before this shipped as well as for new ones. The
      // product page re-sanitises at render, so a description stored with the
      // older sandbox is repaired on the way to the buyer rather than needing
      // the seller to re-save it.
      el.setAttribute('sandbox', EMBED_SANDBOX);
      el.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      el.setAttribute('loading', 'lazy');
      el.setAttribute('allow', 'accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen');
      el.removeAttribute('frameborder');
      // A frame with no accessible name is announced as a bare "frame" in the
      // page's frame list, which is WCAG 4.1.2 and axe-core's frame-title rule.
      // `title` was already on ALLOWED_ATTR in anticipation of this, but the
      // editor never produced one, so every embed shipped nameless. Set here
      // rather than on the Tiptap node so documents written before this get a
      // name too, and only when absent so a real caption is never overwritten.
      if (!el.getAttribute('title')) {
        el.setAttribute('title', /player\.vimeo\.com/.test(el.getAttribute('src') || '') ? 'Embedded Vimeo video' : 'Embedded YouTube video');
      }
      enforceMediaWidth(el);
    }
  });
}

/**
 * Enforce the alignment, spacing and list-start rules above on every element
 * that survives the pass.
 *
 * A hook of its own rather than a branch of the media hook, because `align`
 * and the spacing attributes are legal on paragraphs and headings (and `start`
 * on an ordered list), and that hook only ever looks at `IMG` and `IFRAME`.
 * Registered by every entry point. The strict pass needs it since the spacing
 * attributes joined its allowlist (tier descriptions have the spacing
 * control): without it, a page that only ever rendered tier descriptions would
 * keep whatever spacing value a forged write stored. `align` and `start` are
 * still off the strict list, so DOMPurify removes them before this runs.
 *
 * Registered once per process, like the two hooks around it: `addHook` appends,
 * so a per-call registration would stack a fresh copy on every render.
 */
let blockAttributeHookRegistered = false;
function registerBlockAttributeHook(): void {
  if (blockAttributeHookRegistered) return;
  blockAttributeHookRegistered = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const el = node as unknown as Element;
    if (typeof el.getAttribute !== 'function') return;
    enforceAlignment(el);
    enforceBlockSpacing(el);
    enforceListStart(el);
  });
}

/**
 * Force `rel="noopener noreferrer"` onto every anchor that carries a `target`.
 *
 * `target` and `rel` are both on ALLOWED_ATTR, and DOMPurify does NOT pair them
 * for you, so the author controls both: `<a target="_blank" rel="opener">` is a
 * legal review, product description or FAQ answer today. Every evergreen browser
 * now implies `noopener` for `target="_blank"`, which is why this is residual
 * rather than live — but an explicit `rel="opener"` asks the browser to turn
 * that default back off, and the referrer leak has no such default at all.
 *
 * Done as a hook rather than a config key because there is no config key for
 * it: DOMPurify can allow or forbid `rel`, not rewrite it. The hook runs after
 * attribute sanitization, so the value set here is ours and final.
 *
 * Registered once per process. `addHook` appends, so calling it on every
 * sanitize() would stack a fresh copy of the same hook on every render.
 */
let anchorRelHookRegistered = false;
function registerAnchorRelHook(): void {
  if (anchorRelHookRegistered) return;
  anchorRelHookRegistered = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    const el = node as unknown as Element;
    if (typeof el.hasAttribute !== 'function') return;
    if (el.nodeName !== 'A' || !el.hasAttribute('target')) return;
    el.setAttribute('rel', 'noopener noreferrer');
  });
}

/**
 * Sanitize user-authored HTML. Safe to call from Server Components, Client
 * Components, and route handlers — `isomorphic-dompurify` resolves to the
 * browser DOM in the client bundle and to a jsdom instance in Node.
 */
export function sanitizeHtml(dirty: string): string {
  registerAnchorRelHook();
  // Validates the spacing values the strict list now admits; see
  // registerBlockAttributeHook.
  registerBlockAttributeHook();
  return DOMPurify.sanitize(dirty, STRICT_HTML_CONFIG) as unknown as string;
}

/**
 * Sanitize a product's `longDescription`, one of the two fields allowed
 * images, tables, rules and video embeds (profile notes, through
 * `sanitizeNoteHtml` below, are the other). A user's bio goes through
 * `sanitizeBioHtml`, and every other HTML-bearing field through `sanitizeHtml`
 * above.
 *
 * Mirrored by `sanitizeRichHtml` in functions/src/lib/sanitize-html.ts, which
 * runs the same pass on write. Keep the two allowlists in step: that one stops
 * a hostile direct-to-Firestore write from being STORED, this one protects
 * every document written before it existed at RENDER time.
 */
export function sanitizeRichHtml(dirty: string): string {
  registerAnchorRelHook();
  registerMediaSourceHook();
  registerBlockAttributeHook();
  richImageBudget = DESCRIPTION_IMAGE_MAX_COUNT;
  richEmbedBudget = DESCRIPTION_EMBED_MAX_COUNT;
  try {
    return DOMPurify.sanitize(
      (dirty || '').slice(0, DESCRIPTION_HTML_MAX_LENGTH),
      RICH_HTML_CONFIG,
    ) as unknown as string;
  } finally {
    richImageBudget = 0;
    richEmbedBudget = 0;
  }
}

/**
 * Sanitize a seller's profile note, for display and before it is saved.
 *
 * Notes are written in the same rich editor as a product's long description
 * (owner decision 2026-10-09), so they go through the same pass with the same
 * per-note caps: images only from our own bucket and at most
 * DESCRIPTION_IMAGE_MAX_COUNT of them, at most DESCRIPTION_EMBED_MAX_COUNT
 * players, each hardened. Like the bio, a note is written straight from the
 * client to the world-readable user document with no Firestore trigger in the
 * path, so this render-time pass is the only gate on its markup, and every
 * surface that renders a note goes through NoteBody
 * (src/components/profile/profile-notes.tsx), which calls this.
 *
 * Pass the stored `text` as it is. Notes written before the editor are plain
 * text, and `noteHtml` escapes those into paragraphs first, so "a <b> c" stays
 * text instead of being read as markup. The per-note length cap is applied
 * before this, by normalizeNotes on the way off the document.
 */
export function sanitizeNoteHtml(stored: string): string {
  return sanitizeRichHtml(noteHtml(stored));
}

/**
 * `sanitizeRichHtml` for the SELLER'S OWN page preview in the product editor,
 * where images picked in this session are still `blob:` URLs. Never use it for
 * anything a buyer sees or anything that gets stored — see
 * PREVIEW_RICH_CONFIG_INTERNAL for the full reasoning.
 */
export function sanitizeRichHtmlForPreview(dirty: string): string {
  registerAnchorRelHook();
  registerMediaSourceHook();
  registerBlockAttributeHook();
  allowLocalImageSources = true;
  // The same budgets and the same length cap the buyer's page gets, so the
  // seller's own preview shows them the trim rather than showing six images (or
  // a sixth embed) and letting it disappear on save.
  richImageBudget = DESCRIPTION_IMAGE_MAX_COUNT;
  richEmbedBudget = DESCRIPTION_EMBED_MAX_COUNT;
  try {
    return DOMPurify.sanitize(
      (dirty || '').slice(0, DESCRIPTION_HTML_MAX_LENGTH),
      PREVIEW_RICH_CONFIG,
    ) as unknown as string;
  } finally {
    allowLocalImageSources = false;
    richImageBudget = 0;
    richEmbedBudget = 0;
  }
}

/**
 * Sanitize a user's `bio` for display: the text marks, headings and lists,
 * plus the alignment and spacing attributes the bio editor writes, and
 * nothing else. See BIO_HTML_CONFIG_INTERNAL for what is left out and why.
 *
 * Pass it the output of `bioHtml()` (src/lib/profile-bio.ts), not the raw
 * stored string: bios written before the editor are plain text, and it is
 * bioHtml that escapes those into paragraphs instead of letting this pass read
 * "a <b> c" as markup. Render through ProfileBio rather than calling this at a
 * render site, so the dashboard, the public profile and the server-rendered
 * copy cannot drift.
 *
 * Also what the dashboard should run on the editor's output before saving it,
 * so the stored bio is already the sanitized form and a save of an unedited
 * bio writes the same bytes back.
 *
 * No length cap here. firestore.rules bounds the stored field (bioValid), and
 * bioHtml slices to the same number before converting, so a legacy plain-text
 * bio that grows as it is escaped is never cut mid-sentence by this pass.
 */
export function sanitizeBioHtml(dirty: unknown): string {
  if (typeof dirty !== 'string' || !dirty) return '';
  // The anchor hook acts on nothing today (`a` is not on the bio list); it is
  // registered anyway so that adding links to the bio later cannot ship
  // without the forced rel.
  registerAnchorRelHook();
  registerBlockAttributeHook();
  return DOMPurify.sanitize(dirty, BIO_HTML_CONFIG) as unknown as string;
}

/**
 * A stored bio's VISIBLE text, as one line: the text of exactly what ProfileBio
 * renders. For every place that needs the bio as text rather than markup (the
 * meta, OG and Twitter descriptions, the Person JSON-LD) and for a render site
 * deciding whether there is a bio to draw at all.
 *
 * WHY NOT bioPlainText ON ITS OWN. bioPlainText strips tags with a regex and
 * keeps the text of every element, including the ones sanitizeBioHtml removes
 * together with their content (`template`, `script`, `style`, `noscript`, the
 * children of `svg` and `math`). The bio is client-written to a world-readable
 * document, so a forged one could carry a line the page never shows,
 * "<template>claim your refund at evil.example</template>", straight into the
 * link preview and the search snippet under supa.place's name, with nothing on
 * the page for a moderator to find. Sanitizing first means the text can only
 * ever be text the page itself renders. Not an XSS fix: both sinks escape.
 *
 * Returns TEXT with entities decoded; escape it as text wherever it lands.
 */
export function bioDisplayText(stored: unknown): string {
  return bioPlainText(sanitizeBioHtml(bioHtml(stored)));
}
